import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/MainLayout';
import api from '../../lib/api';
import { usePrivacy } from '../../lib/privacy';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Gauge, Zap, TrendingUp, BatteryCharging, AlertTriangle, Download,
  RefreshCw, Activity, ChevronLeft, ChevronRight, FileText,
} from 'lucide-react';

const PAGE_SIZE = 50;

// TEMPORARILY HIDDEN (Jun 2026): Load Factor & Power Factor surfaced unreliable
// values (e.g. literal "\u2014" junk + miscomputed LF) for some meters. Hidden
// across the Demand report until the deterministic LF/PF computation is trusted.
// Flip back to true to re-activate the KPI tiles, commodity rows, table columns
// and sort options. (Backlog: "Re-activate Demand Load Factor / Power Factor".)
const SHOW_FACTORS = false;

const num = (v, d = 0) =>
  v == null ? '\u2014' : Number(v).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
const money = (v) => (v == null ? '\u2014' : `$${num(v, 2)}`);
const fmtDate = (v) => {
  if (!v) return '\u2014';
  const d = new Date(v);
  return isNaN(d) ? v : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
};

const isAdmin = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return !!(u.is_super_admin || u.is_owner || (u.role || '').toLowerCase() === 'admin');
  } catch { return false; }
};

const KpiTile = ({ icon: Icon, label, value, sub, tone = 'default', testid }) => {
  const tones = {
    default: 'border-border',
    amber: 'border-amber-200 bg-amber-50/50',
    red: 'border-red-200 bg-red-50/50',
    emerald: 'border-emerald-200 bg-emerald-50/50',
  };
  return (
    <Card className={`p-4 rounded-none ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wide">
        <Icon className="h-4 w-4" /> {label}
      </div>
      <div className="mt-2 text-2xl font-bold font-mono">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </Card>
  );
};

const FactorCell = ({ value, type, kind }) => {
  if (value == null) return <span className="text-muted-foreground">{'\u2014'}</span>;
  const low = kind === 'pf' ? value < 0.9 : value < 40;
  const display = kind === 'pf' ? Number(value).toFixed(3) : `${value}%`;
  return (
    <span className={`inline-flex items-center gap-1 rounded-none border px-1.5 py-0.5 text-xs font-medium ${
      low ? 'bg-red-50 text-red-700 border-red-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'
    }`}>
      {display}<span className="opacity-60 text-[9px] uppercase">{type === 'billed' ? 'bill' : 'calc'}</span>
    </span>
  );
};

export default function DemandReport() {
  const navigate = useNavigate();
  const privacy = usePrivacy();
  const [currentAccount] = useState(() => {
    const s = localStorage.getItem('current_account');
    return s ? JSON.parse(s) : null;
  });
  const accountId = currentAccount?.id;

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [options, setOptions] = useState({ commodities: [], vendors: [] });
  const [backfilling, setBackfilling] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [commodity, setCommodity] = useState('all');
  const [vendor, setVendor] = useState('all');
  const [demandOnly, setDemandOnly] = useState(true);
  const [sortBy, setSortBy] = useState('invoice_date');

  const buildParams = useCallback((extra = {}) => {
    const p = { account_id: accountId };
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    if (commodity !== 'all') p.commodity = commodity;
    if (vendor !== 'all') p.vendor_name = vendor;
    if (demandOnly) p.has_demand = true;
    return { ...p, ...extra };
  }, [accountId, dateFrom, dateTo, commodity, vendor, demandOnly]);

  const load = useCallback(async (newOffset = 0) => {
    if (!accountId) return;
    setLoading(true);
    try {
      const [s, m] = await Promise.all([
        api.get('/demand/summary', { params: buildParams() }),
        api.get('/demand/meters', { params: buildParams({ limit: PAGE_SIZE, offset: newOffset, sort_by: sortBy, sort_dir: -1 }) }),
      ]);
      setSummary(s.data);
      setRows(m.data.rows || []);
      setTotal(m.data.total || 0);
      setOffset(newOffset);
    } catch (e) {
      toast.error('Failed to load demand report');
    } finally {
      setLoading(false);
    }
  }, [accountId, buildParams, sortBy]);

  useEffect(() => {
    if (!accountId) return;
    api.get('/demand/filter-options', { params: { account_id: accountId } })
      .then((r) => setOptions(r.data)).catch(() => {});
  }, [accountId]);

  useEffect(() => { load(0); /* eslint-disable-next-line */ }, [accountId, sortBy]);

  const applyFilters = () => load(0);

  const runBackfill = async () => {
    const aid = accountId || (() => {
      try { return JSON.parse(localStorage.getItem('current_account') || '{}').id; } catch { return null; }
    })();
    if (!aid) {
      toast.error('Select an organization first');
      return;
    }
    setBackfilling(true);
    try {
      const res = await api.post('/demand/backfill', null, { params: { account_id: aid, limit: 20000 } });
      if (res?.data?.status === 'started') {
        toast.success('Backfill started — metrics will appear shortly. Refresh in a moment.');
      } else {
        toast.success('Backfill triggered.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Backfill failed');
    } finally {
      setBackfilling(false);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get('/demand/export.csv', { params: buildParams(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'demand_power_quality.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  if (!accountId) {
    return (
      <MainLayout>
        <div className="p-6 max-w-[1600px] mx-auto">
          <Card className="p-10 rounded-none text-center" data-testid="demand-no-account">
            <Gauge className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
            <h2 className="text-lg font-bold">No organization selected</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Pick an organization from the account switcher to view its Demand & Power Quality report.
            </p>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-6 space-y-6 max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Gauge className="h-6 w-6 text-amber-600" />
            <div>
              <h1 className="text-2xl font-bold">Demand & Power Quality</h1>
              <p className="text-sm text-muted-foreground">
                {SHOW_FACTORS
                  ? 'Deterministic Load Factor, Power Factor & demand metrics across your portfolio'
                  : 'Deterministic demand metrics across your portfolio'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin() && (
              <Button variant="outline" className="rounded-none" onClick={runBackfill} disabled={backfilling} data-testid="demand-backfill-btn">
                <RefreshCw className={`h-4 w-4 mr-1 ${backfilling ? 'animate-spin' : ''}`} /> Backfill
              </Button>
            )}
            <Button variant="outline" className="rounded-none" onClick={exportCsv} disabled={exporting} data-testid="demand-export-btn">
              <Download className="h-4 w-4 mr-1" /> Export CSV
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="p-4 rounded-none">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
            <div>
              <label className="text-xs text-muted-foreground">From</label>
              <Input type="date" className="rounded-none mt-1" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="demand-filter-from" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">To</label>
              <Input type="date" className="rounded-none mt-1" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="demand-filter-to" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Commodity</label>
              <Select value={commodity} onValueChange={setCommodity}>
                <SelectTrigger className="rounded-none mt-1" data-testid="demand-filter-commodity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All commodities</SelectItem>
                  {options.commodities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Vendor</label>
              <Select value={vendor} onValueChange={setVendor}>
                <SelectTrigger className="rounded-none mt-1" data-testid="demand-filter-vendor"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">All vendors</SelectItem>
                  {options.vendors.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Scope</label>
              <Select value={demandOnly ? 'demand' : 'all'} onValueChange={(v) => setDemandOnly(v === 'demand')}>
                <SelectTrigger className="rounded-none mt-1" data-testid="demand-filter-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="demand">Demand meters only</SelectItem>
                  <SelectItem value="all">All meters</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button className="rounded-none" onClick={applyFilters} data-testid="demand-apply-filters">Apply</Button>
          </div>
        </Card>

        {/* KPIs */}
        {loading && !summary ? (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-none" />)}
          </div>
        ) : summary && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <KpiTile icon={Activity} label="Demand Meters" value={num(summary.demand_meter_count)} sub={`${num(summary.meter_count)} total`} testid="kpi-demand-meters" />
            <KpiTile icon={BatteryCharging} label="Demand Charges" value={money(summary.total_demand_charge)} tone="amber" testid="kpi-demand-charges" />
            <KpiTile icon={TrendingUp} label="Max Demand" value={summary.max_billing_demand != null ? `${num(summary.max_billing_demand, 1)} kW` : '\u2014'} testid="kpi-max-demand" />
            {SHOW_FACTORS && (<>
            <KpiTile icon={Zap} label="Avg Load Factor" value={summary.avg_load_factor != null ? `${summary.avg_load_factor}%` : '\u2014'} sub={`${summary.low_lf_count} below ${summary.thresholds?.low_lf}%`} tone={summary.low_lf_count ? 'red' : 'emerald'} testid="kpi-avg-lf" />
            <KpiTile icon={Gauge} label="Avg Power Factor" value={summary.avg_power_factor != null ? summary.avg_power_factor : '\u2014'} sub={`${summary.low_pf_count} below ${summary.thresholds?.low_pf}`} tone={summary.low_pf_count ? 'red' : 'emerald'} testid="kpi-avg-pf" />
            <KpiTile icon={AlertTriangle} label="Low PF Meters" value={num(summary.low_pf_count)} sub="potential penalties" tone={summary.low_pf_count ? 'red' : 'default'} testid="kpi-low-pf" />
            </>)}
          </div>
        )}

        {/* Commodity breakdown */}
        {summary?.by_commodity?.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {summary.by_commodity.map((b) => (
              <Card key={b.commodity} className="p-4 rounded-none" data-testid={`demand-commodity-${b.commodity}`}>
                <div className="text-sm font-bold">{b.commodity}</div>
                <div className="mt-2 space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-muted-foreground">Meters</span><span className="font-mono">{num(b.meters)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Demand $</span><span className="font-mono">{money(b.demand_charge)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Max demand</span><span className="font-mono">{b.max_demand != null ? `${num(b.max_demand, 1)} kW` : '\u2014'}</span></div>
                  {SHOW_FACTORS && (<>
                  <div className="flex justify-between"><span className="text-muted-foreground">Avg LF</span><span className="font-mono">{b.avg_load_factor != null ? `${Number(b.avg_load_factor).toFixed(1)}%` : '\u2014'}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Avg PF</span><span className="font-mono">{b.avg_power_factor != null ? Number(b.avg_power_factor).toFixed(3) : '\u2014'}</span></div>
                  </>)}
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Meters table */}
        <Card className="rounded-none">
          <div className="flex items-center justify-between p-3 border-b">
            <h3 className="text-sm font-bold">Per-Meter Metrics ({num(total)})</h3>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="rounded-none w-48 h-8 text-xs" data-testid="demand-sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="invoice_date">Sort: Invoice date</SelectItem>
                <SelectItem value="billing_demand">Sort: Billing demand</SelectItem>
                <SelectItem value="demand_charge_total">Sort: Demand charges</SelectItem>
                {SHOW_FACTORS && (<>
                <SelectItem value="load_factor">Sort: Load factor</SelectItem>
                <SelectItem value="power_factor">Sort: Power factor</SelectItem>
                </>)}
              </SelectContent>
            </Select>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12 text-center">Bill</TableHead>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Commodity</TableHead>
                  <TableHead>Service Address</TableHead>
                  <TableHead>Meter</TableHead>
                  <TableHead className="text-right">Energy</TableHead>
                  <TableHead className="text-right">Billing Demand</TableHead>
                  <TableHead className="text-right">Peak</TableHead>
                  <TableHead className="text-right">Demand $</TableHead>
                  {SHOW_FACTORS && (<>
                  <TableHead className="text-center">Load Factor</TableHead>
                  <TableHead className="text-center">Power Factor</TableHead>
                  </>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={i}><TableCell colSpan={SHOW_FACTORS ? 12 : 10}><Skeleton className="h-5 w-full" /></TableCell></TableRow>
                  ))
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={SHOW_FACTORS ? 12 : 10} className="text-center text-muted-foreground py-10">
                    No metrics found. {isAdmin() && 'Run "Backfill" to seed metrics from existing bills.'}
                  </TableCell></TableRow>
                ) : rows.map((r, i) => (
                  <TableRow key={`${r.document_id}-${r.meter_number}-${i}`} data-testid={`demand-row-${i}`}>
                    <TableCell className="text-center">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 rounded-none text-amber-600 hover:text-amber-700 hover:bg-amber-50 disabled:opacity-30"
                        disabled={!r.document_id}
                        title="View bill"
                        aria-label="View bill"
                        data-testid={`demand-view-bill-${i}`}
                        onClick={() => r.document_id && navigate(`/viewer/${r.document_id}`)}
                      >
                        <FileText className="h-4 w-4" />
                      </Button>
                    </TableCell>
                    <TableCell className="text-xs">{fmtDate(r.invoice_date)}</TableCell>
                    <TableCell className="text-xs">{r.vendor_name}</TableCell>
                    <TableCell className="text-xs">{r.commodity}</TableCell>
                    <TableCell className="text-xs max-w-[180px] truncate" title={privacy.address(r.service_address)}>{privacy.address(r.service_address) || '\u2014'}</TableCell>
                    <TableCell className="text-xs font-mono">{r.meter_number || '\u2014'}</TableCell>
                    <TableCell className="text-xs text-right font-mono">{r.energy_kwh != null ? num(r.energy_kwh) : '\u2014'}</TableCell>
                    <TableCell className="text-xs text-right font-mono">{r.billing_demand != null ? `${num(r.billing_demand, 1)} ${r.demand_uom || 'kW'}` : '\u2014'}</TableCell>
                    <TableCell className="text-xs text-right font-mono">{r.measured_peak_demand != null ? num(r.measured_peak_demand, 1) : '\u2014'}</TableCell>
                    <TableCell className="text-xs text-right font-mono">{r.demand_charge_total ? money(r.demand_charge_total) : '\u2014'}</TableCell>
                    {SHOW_FACTORS && (<>
                    <TableCell className="text-center"><FactorCell value={r.load_factor} type={r.load_factor_type} kind="lf" /></TableCell>
                    <TableCell className="text-center"><FactorCell value={r.power_factor} type={r.power_factor_type} kind="pf" /></TableCell>
                    </>)}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {/* Pagination */}
          <div className="flex items-center justify-between p-3 border-t text-sm">
            <span className="text-muted-foreground">Page {page} of {pages}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="rounded-none" disabled={offset === 0 || loading} onClick={() => load(offset - PAGE_SIZE)} data-testid="demand-prev-page">
                <ChevronLeft className="h-4 w-4" /> Prev
              </Button>
              <Button variant="outline" size="sm" className="rounded-none" disabled={offset + PAGE_SIZE >= total || loading} onClick={() => load(offset + PAGE_SIZE)} data-testid="demand-next-page">
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
}
